import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Gauge, useGaugeState } from '@mui/x-charts/Gauge';

function Needle({ color }) {
  const { valueAngle, cx, cy, outerRadius } = useGaugeState();
  if (valueAngle === null) return null;

  const x = cx + outerRadius * Math.sin(valueAngle);
  const y = cy - outerRadius * Math.cos(valueAngle);

  return (
    <g>
      <line x1={cx} y1={cy} x2={x} y2={y} stroke={color} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={4} fill={color} />
    </g>
  );
}

const difficultyLabels = {
  1: 'Very Easy',
  2: 'Easy',
  3: 'Moderate',
  4: 'Hard',
  5: 'Very Difficult',
};

const DifficultyGauge = ({ value = 0, onChange, size = 120 }) => {
  const theme = useTheme();
  const [hoverValue, setHoverValue] = useState(0);
  const currentValue = hoverValue || value;

  const getColorForValue = (val) => {
    if (val <= 1) return theme.palette.success.light;
    if (val === 2) return theme.palette.success.main;
    if (val === 3) return theme.palette.secondary.light;
    if (val === 4) return theme.palette.error.light;
    return theme.palette.error.main;
  };

  const labelText = currentValue ? `${currentValue} - ${difficultyLabels[currentValue]}` : '';

return (
  <Box sx={{ display: 'flex', alignItems: 'center', mb:-3, mt:-2 }}>
    {/* Gauge with overlays */}
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <Gauge
        value={currentValue ? currentValue * 20 : null}
        valueMin={0}
        valueMax={100}
        startAngle={-90}
        endAngle={90}
        innerRadius="60%"
        outerRadius="100%"
        sx={{
          '& .MuiGauge-valueArc': {
            fill: getColorForValue(currentValue),
          },
          '& .MuiGauge-valueText': {
            display: 'none',
          },
        }}
      >
        <Needle color={theme.palette.info.main} />
      </Gauge>

      {/* Clickable segments for interaction */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
        }}
      >
        {[1, 2, 3, 4, 5].map((val) => (
          <Box
            key={val}
            role="button"
            data-testid={`difficulty-${val}`}
            onMouseEnter={() => setHoverValue(val)}
            onMouseLeave={() => setHoverValue(0)}
            onClick={() => onChange(val)}
            sx={{ flex: 1 }}
          />
        ))}
      </Box>
    </Box>

    {/* Label to the right */}
    {labelText && (
      <Box sx={{ ml: 2, minWidth: 100 }}>
        <Box
          sx={{
            fontSize: 'default',
            fontWeight: 500,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
          }}
        >
          {labelText}
        </Box>
      </Box>
    )}
  </Box>
);
};

export default DifficultyGauge;
